import { getTooltipAttributes } from "@common/contexts/Tooltip";
import { getTourAttributes } from "@common/contexts/Tour";
import Icon from "@common/controls/Icon/component";
import Preloader from "@common/controls/Preloader/component";
import withValueHOC, { type WithValueProps } from "@common/hocs/withValue";
import { useI18n } from "@common/hooks/i18n";
import { copyToClipboard } from "@common/lib/clipboard";
import toasts from "@common/lib/toast";
import { Common } from "@common/types";
import classnames from "classnames";
import * as React from "react";

import "./style.less";

export type TextInputAutoComplete =
  | "tel"
  | "given-name"
  | "family-name"
  | "name"
  | "email"
  | "off"
  | "one-time-code"
  | "username"
  | "street-address"
  | "address-line1"
  | "address-line2"
  | "address-line3"
  | "address-line4"
  | "address-level1"
  | "address-level2"
  | "address-level3"
  | "address-level4"
  | "country"
  | "organization"
  | "postal-code";

export type TextInputInnerProps = Common.InputTypeProps<string> & {
  addon?: any;
  autoComplete?: TextInputAutoComplete;
  autoFocus?: boolean;
  autoSelectOnFocus?: boolean;
  blurOnEnter?: boolean;
  blurOnEscape?: boolean;
  children?: any;
  className?: string;
  copyIfReadonlyValue?: string;
  defaultValue?: string;
  readonlyTheme?: "default" | "light";
  focusedValue?: string;
  tourStep?: string;
  id?: string;
  inputClassName?: string;
  isAutoWidth?: boolean;
  isBusy?: boolean;
  isDisabled?: boolean;
  isReadonly?: boolean;
  isRequired?: boolean;
  isNoCopy?: boolean;
  max?: number | null;
  maxLength?: number;
  isMaxLengthVisible?: boolean;
  min?: number | null;
  name?: string;
  onBlur?: () => void;
  onClick?: (e: React.MouseEvent) => void;
  onEnter?: (e: React.KeyboardEvent) => void;
  onEscape?: (e: React.KeyboardEvent) => void;
  onFocus?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onPaste?: (e: React.ClipboardEvent) => void;
  pattern?: string /* own logic */;
  prepend?: any;
  placeholder?: string;
  preventSubmit?: boolean;
  ref?: React.RefObject<HTMLInputElement>;
  stopPropagation?: boolean;
  step?: number | string;
  style?: React.CSSProperties;
  tabIndex?: number;
  tooltip?: string;
  type?: string;
};

const TextInputInner = (props: TextInputInnerProps) => {
  const i18n = useI18n();

  const innerRef = React.useRef<HTMLInputElement>(null);

  React.useImperativeHandle(props.ref, () => innerRef.current!, []);

  const [isFocused, setIsFocused] = React.useState(false);

  const handleCopyClick = React.useCallback(() => {
    if (props.isReadonly && !props.isNoCopy && !props.isBusy) {
      copyToClipboard(props.copyIfReadonlyValue ?? props.value);
      toasts.info(i18n.copiedToClipboard);
    }
  }, [i18n, props.isReadonly, props.isNoCopy, props.isBusy, props.copyIfReadonlyValue, props.value]);

  const onBlur = React.useCallback(() => {
    props.onBlur && props.onBlur();

    setIsFocused(false);
  }, [props.onBlur]);

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      props.onChange(e.target.value);
    },
    [props.stopPropagation, props.onChange]
  );

  const onFocus = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (props.isReadonly) {
        e.preventDefault();
        return;
      }

      props.onFocus && props.onFocus();

      setIsFocused(true);

      if (props.autoSelectOnFocus) {
        e.target.select();
      }
    },
    [props.autoSelectOnFocus, props.onFocus, props.isReadonly]
  );

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent): void => {
      if (props.stopPropagation) e.stopPropagation();

      if (e.key === "Enter") {
        if (props.preventSubmit) e.preventDefault();

        if (props.onEnter) props.onEnter(e);

        if (props.blurOnEnter && e.target instanceof HTMLInputElement) {
          e.target.blur();
        }
      }

      if (e.key === "Escape") {
        if (props.onEscape) props.onEscape(e);

        if (props.blurOnEscape && e.target instanceof HTMLInputElement) {
          e.target.blur();
        }
      }

      if (props.type === "number" && !e.metaKey && !e.ctrlKey && e.key.match(/^[a-zA-Z!@#$%^&*()]$/)) {
        e.preventDefault();
      }

      props.onKeyDown && props.onKeyDown(e);
    },
    [
      props.preventSubmit,
      props.onEnter,
      props.onKeyDown,
      props.blurOnEnter,
      props.blurOnEscape,
      props.onEscape,
      props.type
    ]
  );

  const type: React.HTMLInputTypeAttribute = props.type ?? "text";

  return (
    <div
      className={classnames(
        "text_input_container",
        props.className,
        props.isReadonly ? props.readonlyTheme ?? "default" : null,
        {
          busy: props.isBusy,
          copy: !props.isNoCopy && props.isReadonly,
          focused: isFocused,
          disabled: props.isDisabled,
          empty: !props.value,
          readonly: props.isReadonly,
          pointer: props.isReadonly && !!props.onClick
        }
      )}
      onClick={() => innerRef.current?.focus()}
      style={props.style} // IMPORTANT: style must be on container, because of inner content, such as prepend and addon
      {...getTooltipAttributes(props.tooltip)}
      {...getTourAttributes({ step: props.tourStep })}
    >
      {!!props.prepend && <div className="prepend">{props.prepend}</div>}

      <input
        ref={innerRef}
        autoComplete={props.autoComplete ?? "off"} //switch off autoComplete by default, can not find approach how change dropdown styles
        autoFocus={props.autoFocus}
        className={classnames("text_input", props.inputClassName)}
        data-name={props.qa || props.name}
        disabled={props.isDisabled}
        id={props.id}
        inputMode={type === "number" ? "numeric" : undefined}
        max={props.max || undefined}
        maxLength={props.maxLength}
        min={props.min || undefined}
        name={props.name}
        onBlur={onBlur}
        onChange={onChange}
        onClick={(e) => (handleCopyClick(), props.onClick && props.onClick(e))}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onPaste={props.onPaste}
        pattern={props.pattern}
        placeholder={props.placeholder}
        readOnly={props.isReadonly}
        required={props.isRequired}
        size={props.isAutoWidth ? props.value?.length : undefined}
        step={props.step}
        tabIndex={props.tabIndex}
        type={type === "number" ? "text" : type}
        value={isFocused ? (props.focusedValue ?? props.value) || "" : props.value || ""}
      />

      <div className="addon" onClick={(e) => e.stopPropagation()}>
        {!!props.maxLength && props.isMaxLengthVisible ? `${props.value?.length ?? 0} / ${props.maxLength}` : null}
        {props.addon}
        {props.isReadonly && !props.isNoCopy && !props.isBusy && (
          <Icon
            className="pointer"
            icon={Common.IconType.Copy}
            onClick={handleCopyClick}
            tooltip={i18n.copyToClipboard}
          />
        )}
      </div>

      {props.isBusy && <Preloader isTransparent isFontSize />}
    </div>
  );
};

const TextInput = withValueHOC<string, TextInputInnerProps>(TextInputInner);
export type TextInputProps = TextInputInnerProps & WithValueProps<string>;
export default TextInput;
